import { useEffect, useState } from "react";
import apiClient from "../../../shared/API/apiClient";
import Toast from "../../../shared/components/Toast";
import LocationMappingModal from "./LocationMappingModal";
import ViewLocationsModal from "./ViewLocationModal";

const LocationMappingPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });

  // ── Fetch ─────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/api/reportingyear-location");
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Location mapping fetch error", err);
      showToast(
        err.response?.data?.message || "Failed to load location mappings",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Edit click handler ────────────────────────────────────
  const handleEditClick = (yr) => {
    if (yr.is_locked) {
      showToast("Cannot edit — this reporting year is locked.", "error");
      return;
    }
    setEditRow(yr);
  };

  // ── After modal saves ─────────────────────────────────────
  const handleSaved = (message) => {
    fetchData();
    showToast(message || "Locations mapped successfully", "success");
    setEditRow(null);
  };

  // ── Helpers ───────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderMappedLocations = (yr) => {
    const locations = Array.isArray(yr.location_ids) ? yr.location_ids : [];

    if (locations.length > 0) {
      const count = locations.length;

      return (
        <span
          className="view-link"
          onClick={() => setViewRow(yr)}
          style={{
            cursor: "pointer",
            color: "#007bff",
            fontWeight: "600",
            textDecoration: "underline",
          }}
        >
          View {count} {count === 1 ? "Location" : "Locations"}
        </span>
      );
    }

    if (!yr.is_locked) {
      return (
        <span
          className="map-link"
          onClick={() => setEditRow(yr)}
          style={{ color: "#28a745", cursor: "pointer" }}
        >
          + Add Locations
        </span>
      );
    }

    return <span className="empty-cell">—</span>;
  };



  // ── Render ────────────────────────────────────────────────
  return (
    <div className="dash-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="le-page-header">
        <div>
          <div className="dash-page-title">Location Mapping</div>
          <div className="dash-page-subtitle">
            Map locations to reporting years for BRSR reporting
          </div>
        </div>
      </div>

      {/* List */}
      <div className="le-card">
        {loading ? (
          <div className="le-empty">
            <div className="le-empty-text">Loading location mappings...</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="le-empty">
            <div className="le-empty-text">No reporting years found.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="le-table">
              <thead>
                <tr>
                  <th>Reporting Year</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Mapped Locations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((yr) => {
                  const isLocked = Boolean(yr.is_locked);

                  const hasLocations =
                    Array.isArray(yr.location_ids) &&
                    yr.location_ids.length > 0;

                  return (
                    <tr key={yr.id}>
                      <td data-label="Reporting Year">{yr.year_label}</td>
                      <td data-label="Start Date">
                        {formatDate(yr.start_date)}
                      </td>
                      <td data-label="End Date">{formatDate(yr.end_date)}</td>
                      <td data-label="Status">
                        <span
                          className={`badge ${
                            isLocked ? "locked" : "unlocked"
                          }`}
                        >
                          {isLocked ? "Closed" : "Active"}
                        </span>
                      </td>
                      <td data-label="Mapped Locations">
                        {renderMappedLocations(yr)}
                      </td>
                      <td data-label="Actions">
                        <div className="le-actions">
                          {!isLocked && (
                            <span
                              className="action-item edit"
                              onClick={() => handleEditClick(yr)}
                              title={
                                hasLocations ? "Edit mapping" : "Add mapping"
                              }
                            >
                              <img
                                src={
                                  hasLocations
                                    ? "https://cdn-icons-png.flaticon.com/512/1159/1159633.png"
                                    : "https://cdn-icons-png.flaticon.com/512/992/992651.png"
                                }
                                alt="action"
                                className="action-icon"
                              />
                              {hasLocations ? "Edit" : "Add"}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mapping Modal */}
      {editRow && (
        <LocationMappingModal
          data={editRow}
          onClose={() => setEditRow(null)}
          onSave={handleSaved}
        />
      )}
      {viewRow && (
        <ViewLocationsModal
          title={
            (viewRow.location_ids?.length || 0) === 1
              ? "Mapped Location"
              : "Mapped Locations"
          }
          data={viewRow}
          onClose={() => setViewRow(null)}
        />
      )}
    </div>
  );
};

export default LocationMappingPage;
